import type { Params } from '@jsonfmt/core';
import { type ReactElement, type ReactNode, type Ref, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { Hue } from '@/ui/components/hue';
import { Icon } from '@/ui/components/Icon';

import styles from './StatusPill.module.css';
import type { StatusGlyph, StatusModel } from './statusModel';

/**
 * The verdict, and THE DOOR — ADR-0101 §7.5, §8.2, docs/DESIGN.md §6.11, ADR-0009.
 *
 * It renders a {@link StatusModel} and decides NOTHING about the verdict. Which verdict is
 * shown, what tone it carries and which counted fragments make up its sentence are all settled
 * in `statusModel.ts`, in bare TypeScript, under test. This file turns that record into spans —
 * and, since ADR-0101, hangs the toggle for the diagnostics drawer off the same pill.
 *
 * ═══ WHY THE PILL IS THE TOGGLE ═══
 *
 * ADR-0100 drew the count twice: a "2 problems" verdict in the header and a "Problems 2" panel
 * header underneath it. Two controls, one number, and nothing keeping them in step at a glance.
 * ADR-0101 §0 item 3 collapses them: the verdict IS the control that opens the list, so the
 * count is stated once, in the place a user already looks to find out whether anything is
 * wrong. "Door" is the design's own word for it and the name is kept here so the CSS, the ADR
 * and the tests all say the same thing.
 *
 * ═══ THE LIVE REGION IS NEVER INSIDE THE BUTTON ═══
 *
 * `role="status"` is a sibling of the `<button>`, not its child, and what that buys is that the
 * ANNOUNCEMENT belongs to the region rather than to the control: a live region nested inside a
 * button is re-announced as the button's own name on every update, so a user standing on the
 * door would hear the control instead of the verdict.
 *
 * The button then takes its name by REFERENCE — `aria-labelledby="<the hidden word 'Problems:'>
 * <the status element>"`. Note what that does NOT do: the name is still computed from the text
 * of the live region, so it still changes on every verdict. That is deliberate rather than
 * overlooked. A door labelled "Problems:" and nothing else would fail WCAG 2.5.3 (Label in
 * Name) the moment a speech-input user said the words printed on it, and re-reading a control
 * whose name genuinely changed is the lesser cost. If a screen reader is ever measured
 * re-announcing the door on each keystroke, the fix is to point `aria-labelledby` at a
 * duplicated visually-hidden copy of the sentence — not to take the sentence out of the name.
 *
 * ═══ LABEL IN NAME, AT EVERY WIDTH ═══
 *
 * WCAG 2.5.3 asks that the accessible name CONTAIN the visible label, and the visible label
 * here changes with the viewport: from 600px it is the full sentence ("1 error, 1 warning"),
 * and below 600px it is the short form and only the short form ("2 problems"), which is the
 * redesign's own phone-first surface. The short span shipped `aria-hidden`, so under 600px the
 * name contained none of the three words on the control and "click 2 problems" activated
 * nothing. It is no longer hidden: the name is built from the status element's text, CSS
 * decides which of the two spans is in that text, and neither the markup nor an id list has to
 * restate the breakpoint. Below 600px the name is "Problems: 2 problems 1 error, 1 warning" and
 * from 600px it is "Problems: 1 error, 1 warning" — the visible label is inside it either way.
 *
 * ═══ WHY THE SENTENCE ARRIVES AS FRAGMENTS ═══
 *
 * `model.segments` is a list of `{ key, params }`, not a string, because "2 errors, 1 warning"
 * is three decisions: which counted nouns appear, how each pluralises, and in what order.
 * i18next owns the second (base key + `{ count }`, never `_one` / `_other` by hand — ADR-0018)
 * and the model owns the first and third. A pre-joined string from the feature would have had
 * to pluralise in TypeScript, which is the exact defect ADR-0008 removed from the legacy engine
 * (`` `${n} brace${n > 1 ? 's' : ''}` ``).
 *
 * ═══ THE SHORT FORM, AND WHY IT IS CONDITIONAL ═══
 *
 * Below 600px the pill has room for a glyph, a number and a noun — "2 problems" — and the full
 * breakdown is visually hidden while staying in the sentence a screen reader reads. The short
 * span is rendered ONLY when it differs from the sentence (problems, blocked). For "Valid JSON"
 * and "Empty" the two strings are equal, and drawing both would put a hidden duplicate of the
 * visible text in the DOM — which is what makes Playwright's `getByText('Valid JSON')` throw
 * for strict-mode ambiguity. The rule is stated in ADR-0101 §7.5 and it is why this is a
 * comparison rather than a breakpoint.
 *
 * ═══ THE STALE STATE HAS NO WORD, ON PURPOSE ═══
 *
 * While `reportVersion < version` the pill may not show the previous verdict — it is about
 * different bytes (ADR-0009). It shows a muted ellipsis and `aria-busy="true"`, so a screen
 * reader is told the region is updating rather than read a stale count. It KEEPS ITS HUE
 * through the wait (a ref below): dropping to neutral for 150ms on every keystroke would strobe
 * the header, and the hue is not a verdict — the word is, and the word is what goes away.
 *
 * ═══ SEVERITY IN THREE CHANNELS ═══
 *
 * Shape (filled circle / triangle / hollow ring / check), word, and tone — docs/DESIGN.md §6.4.
 * Shape is first because it is the channel that survives greyscale, colour blindness and
 * `forced-colors: active`; the glyph is `aria-hidden` and the word carries the meaning for
 * anyone not looking at it.
 */

export interface StatusDoor {
  /** Is the drawer open? Drives `aria-expanded` and the chevron's rotation. */
  readonly expanded: boolean;
  /** The `id` of the drawer this toggle controls — `aria-controls`. */
  readonly controls: string;
  /** Localised `a11y.problemsDoor` ("Problems:"). The hidden first word of the name. */
  readonly label: string;
  readonly onToggle: () => void;
  readonly ref?: Ref<HTMLButtonElement>;
}

export interface StatusPillProps {
  readonly model: StatusModel;
  /**
   * Joins the counted fragments. Defaults to `', '`, which is what the design shows
   * ("2 errors, 1 warning"); a locale whose list separator differs passes its own.
   */
  readonly separator?: string;
  /**
   * The drawer toggle. Omitted ⇒ the pill is a plain verdict, which is how the empty state and
   * a future colophon use it. Given, it is still SUPPRESSED for `empty` and `blocked` — see
   * `hasDoor` below.
   */
  readonly door?: StatusDoor;
  /** One pulse of the hue glow, twice over 600ms. Set by the shell; see ADR-0101 §8.3. */
  readonly pulse?: boolean;
  readonly className?: string;
  readonly id?: string;
}

/**
 * ONE MEANING PER HUE (`ui/components/hue.ts`, ADR-0101 §1). The mapping is here and not in
 * `statusModel.ts` because a hue is a presentation decision — the model already states the
 * thing that matters, which is the severity — and because `features/**\/*.ts` is compiled by
 * the DOM-free program and has no business naming a colour family.
 */
function hueOf(model: StatusModel): Hue | undefined {
  switch (model.kind) {
    case 'empty':
      return 'peri';
    case 'valid':
      return 'lime';
    case 'blocked':
      return 'coral';
    case 'problems':
      return model.tone === 'error' ? 'coral' : model.tone === 'warning' ? 'amber' : 'aqua';
    // `stale` keeps whatever the last real verdict was. Answered by the caller's ref.
    default:
      return undefined;
  }
}

const SHAPE: Readonly<Record<Exclude<StatusGlyph, 'check' | 'none'>, string>> = {
  dot: styles.dot ?? '',
  triangle: styles.triangle ?? '',
  ring: styles.ring ?? '',
};

function Glyph({ glyph }: { readonly glyph: StatusGlyph }): ReactNode {
  if (glyph === 'none') return null;
  // The check is the one severity-free glyph, and it is the only one with enough internal
  // detail to be worth an SVG. The other three are 8px primitives: a border-radius, a border
  // triangle and a ring cost nothing and cannot fail to load.
  if (glyph === 'check') return <Icon className={styles.check} name="check" size={14} />;
  return <span aria-hidden="true" className={SHAPE[glyph]} />;
}

export function StatusPill({
  model,
  separator = ', ',
  door,
  pulse,
  className,
  id,
}: StatusPillProps): ReactElement {
  const { t } = useTranslation();
  /**
   * `t` is typed against the whole key space (ADR-0018, `locales/types.d.ts`), but a
   * `StatusSegment.key` is a plain `string`: `features/types.ts` is compiled by the DOM-free
   * program and cannot reference i18next's types. One narrowing cast at the one place a
   * computed key is used beats widening the key space for everybody — `npm run i18n:check`
   * is what proves these particular keys exist (i18n handoff, note 1).
   */
  const translate = t as unknown as (key: string, params?: Params) => string;

  const generatedId = useId();
  const statusId = id ?? `${generatedId}status`;
  const wordId = `${generatedId}word`;

  /**
   * The last hue a REAL verdict had. A ref rather than state: nothing re-renders because of
   * it — it is read during the render that also writes it, and it exists only so the stale
   * state has something to keep wearing.
   */
  const lastHue = useRef<Hue>('peri');
  const hue = hueOf(model) ?? lastHue.current;
  lastHue.current = hue;

  const sentence = model.segments
    .map((segment) => translate(segment.key, segment.params))
    .join(separator);

  /**
   * The under-600px form. `undefined` means "the sentence already fits, draw it alone".
   *
   * `problems` is the only kind that counts, and it counts the TOTAL rather than repeating the
   * breakdown: "2 problems" is one number where the sentence is two, and the sentence is still
   * what is read aloud.
   */
  const short =
    model.kind === 'problems'
      ? t('editor.status.problems', { count: model.count })
      : model.kind === 'blocked'
        ? t('editor.status.blockedShort')
        : undefined;

  /**
   * ADR-0101 §7.5's table: no door over an empty buffer and none past the hard limit.
   *
   * Not "the caller should not pass one" but "this component will not draw one", because both
   * cases are states of the document rather than of the shell: there is no list to open when
   * nothing has been typed, and past 10 MB there is no live analysis to list (ADR-0009). A
   * control that opens an empty drawer is the "0 problems" affordance §0 item 6 removed.
   */
  const hasDoor = door !== undefined && model.kind !== 'empty' && model.kind !== 'blocked';

  return (
    <div
      className={[styles.door, className].filter(Boolean).join(' ')}
      data-hue={hue}
      data-pulse={pulse === true ? '' : undefined}
    >
      {/*
        `role="status"` — docs/DESIGN.md §7.5, third bullet.
        ═════════════════════════════════════════════════════════════════════════════════════
        NOTHING ANNOUNCED THE VERDICT. A screen-reader user typed, the document stopped parsing,
        the pill flipped from "Empty" to "7 errors, 1 warning" and eight rows appeared in the
        problems list, and not one word was spoken: no toast fires for typing, and a grep of the
        shipped bundle found exactly one `role="status"` (the repair banner, which only fires on
        a repair) and one `aria-live` (the toast region). The verdict was reachable — the list
        is real buttons in the tab order — but nobody was told to go and look. That is WCAG 2.1
        SC 4.1.3 (Status Messages), and it is a rule this project wrote down for itself.

        `aria-atomic`, because the pill's segments are rebuilt from scratch on every verdict and
        a partial re-read ("1 warning") would be a different, wrong sentence. `aria-busy` is
        justified in this file's own header by "a screen reader is told the region is updating
        rather than read a stale count" — behaviour that only exists once the element IS a live
        region.
      */}
      <span
        role="status"
        aria-atomic="true"
        aria-busy={model.kind === 'stale' ? true : undefined}
        className={styles.pill}
        data-kind={model.kind}
        id={statusId}
      >
        <Glyph glyph={model.glyph} />
        {model.segments.length === 0 ? (
          // No verdict to show. The ellipsis is punctuation, not a word, so it needs no
          // translation and cannot go stale.
          <span aria-hidden="true" className={styles.pending}>
            …
          </span>
        ) : (
          <>
            {short === undefined ? null : (
              <>
                {/* NOT `aria-hidden` — see "LABEL IN NAME" in the header. Below 600px this is
                    the only text a sighted user can see on the door, so it has to be part of
                    the door's name; from 600px CSS sets `display: none` on it and the name
                    computation drops it again, with no attribute here knowing the breakpoint.

                    The space after it is for the NAME, not for the layout: the two spans are
                    adjacent element children and an accessible name concatenates them, so
                    without it a phone user hears "2 problems1 error, 1 warning". `.pill` is a
                    flex container and a whitespace-only text node in one is not rendered at
                    all, so nothing on screen moves. */}
                <span className={styles.short}>{short}</span>{' '}
              </>
            )}
            {/* Visually hidden below 600px by CSS, never removed: it is the sentence the live
                region announces and the text the door's name is built from. */}
            <span className={short === undefined ? styles.plain : styles.text}>{sentence}</span>
          </>
        )}
      </span>

      {hasDoor ? (
        <button
          aria-controls={door.controls}
          aria-expanded={door.expanded}
          // Name by reference, so the button's own subtree stays empty and the live region
          // keeps its announcements to itself. See the header.
          aria-labelledby={`${wordId} ${statusId}`}
          // Advertised rather than implied: F8 opens this same drawer from anywhere on the page
          // (`features/lint/commands.ts`), and a keyboard user has no other way to learn it.
          aria-keyshortcuts="F8"
          className={styles.toggle}
          onClick={door.onToggle}
          ref={door.ref}
          type="button"
        >
          <span hidden id={wordId}>
            {door.label}
          </span>
          <Icon name="chevron-down" size={16} />
        </button>
      ) : null}
    </div>
  );
}

export default StatusPill;
